import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { numberWithPoint } from '../../utils/utils';
import { ButtonsPrimary } from '../ComponentApp/buttoms';
import Spinner from '../Spinner';
import { ContributionsSectionTitleForm } from './SimulationComponents/ContributionsSectionTitleForm';
import {
  BackArrowContainer,
  BoxWrapper,
  ButtonWrapper,
  ContainerForm,
  ContentFormContributions,
  ImgStyled,
  TextStyled,
} from './SimulationComponents/SimulationMuiComponents';
import { FormatCurrency } from '../../utils/numbers';

const SimulatorFormFunds = ({
  handleSubmit,
  formData,
  loadingSimulationFunds,
  securitySelected,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const result = securitySelected || null;

  const safeResult = result || {};

  let custom = null;
  if (safeResult?.Custom) {
    try {
      custom =
        typeof safeResult.Custom === 'string' ? JSON.parse(safeResult.Custom) : safeResult.Custom;
    } catch (err) {
      console.warn('Error parsing Custom for security', err);
      custom = null;
    }
  }

  const minInvestmentAmount = custom?.fund_details?.min_investment_amount;

  const initialValues = {
    initialInvestment: '',
    monthlyInvestment: '',
    months: formData.months || '1 año',
  };

  const validationSchema = Yup.object({
    initialInvestment: Yup.string().required('El campo es requerido'),
    monthlyInvestment: Yup.string(),
    months: Yup.string().required('El campo es requerido'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,

    validate: values => {
      const minAmount = minInvestmentAmount;
      const errors = {};

      if (
        !values.initialInvestment ||
        Number.parseInt(values.initialInvestment.replace(/\D/g, '')) < minAmount ||
        values.initialInvestment[0] === '0'
      ) {
        errors.initialInvestment = `El monto debe ser mayor a ${FormatCurrency(minAmount)}`;
      }

      if (Number.parseInt(values.monthlyInvestment?.replace(/\D/g, '')) < minAmount) {
        errors.monthlyInvestment = `El aporte mensual debe ser mayor a ${FormatCurrency(minAmount)}`;
      }

      return errors;
    },
    enableReinitialize: true,
  });

  const optionsYears = [
    { value: '6 meses' },
    { value: '1 año' },
    { value: '2 años' },
    { value: '3 años' },
  ];

  if (loadingSimulationFunds) {
    return <Spinner />;
  }

  return (
    <>
      <BackArrowContainer>
        <Button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', padding: 0 }}>
          <ImgStyled src='/assets/images/Arrow1.svg' alt='Arrow' />
        </Button>
      </BackArrowContainer>

      <ContentFormContributions maxWidth={'720px'}>
        <ContributionsSectionTitleForm textTitle={'Simula tu inversión en el fondo'} />

        <ContainerForm>
          <BoxWrapper>
            <Box>
              <TextField
                name='initialInvestment'
                label={
                  <Typography
                    sx={{
                      fontSize: theme.typography.mediumText.fontSize,
                      [theme.breakpoints.down('sm')]: {
                        fontSize: theme.typography.customButton.fontSize,
                      },
                    }}>
                    Inversión Inicial
                  </Typography>
                }
                variant='standard'
                fullWidth
                helperText={formik.touched.initialInvestment && formik.errors.initialInvestment}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                }}
                error={Boolean(formik.touched.initialInvestment && formik.errors.initialInvestment)}
                value={formik.values.initialInvestment || ''}
                onBlur={formik.handleBlur}
                onChange={e => {
                  const value = e.target.value;
                  const validAmount = value.replace(/\D/g, '');
                  if (validAmount.toString().length <= 8) {
                    const format = numberWithPoint(validAmount);
                    formik.setFieldValue('initialInvestment', format);
                  }
                }}
              />
              <TextStyled
                sx={{
                  width: '100%',
                }}>
                Inversión mínima del fondo es de {FormatCurrency(minInvestmentAmount)}
              </TextStyled>
              <TextField
                sx={{
                  mb: '22px',
                }}
                name='monthlyInvestment'
                label={
                  <Typography
                    sx={{
                      fontSize: theme.typography.mediumText.fontSize,
                      [theme.breakpoints.down('sm')]: {
                        fontSize: theme.typography.customButton.fontSize,
                      },
                    }}>
                    Aporte Mensual (Opcional)
                  </Typography>
                }
                variant='standard'
                fullWidth
                error={Boolean(formik.touched.monthlyInvestment && formik.errors.monthlyInvestment)}
                onBlur={formik.handleBlur}
                helperText={formik.touched.monthlyInvestment && formik.errors.monthlyInvestment}
                onChange={e => {
                  const value = e.target.value;
                  const validAmount = value.replace(/\D/g, '');
                  if (validAmount.toString().length <= 8) {
                    const format = numberWithPoint(validAmount);
                    formik.setFieldValue('monthlyInvestment', format);
                  }
                }}
                value={formik.values.monthlyInvestment || ''}
              />
              <Box sx={{ width: '100%' }}>
                <FormControl variant='standard' sx={{ width: '100%' }}>
                  <InputLabel id='demo-simple-select-standard-label'>
                    Plazo estimado de inversión (horizonte)
                  </InputLabel>
                  <Select
                    label='Age'
                    sx={{
                      width: '100%',
                      fontSize: theme.typography.availableBalance.fontSize,
                      color: theme.palette.textLight.main,
                    }}
                    id='months'
                    value={formik.values.months}
                    variant='standard'
                    onChange={e => {
                      formik.setFieldValue('months', e.target.value);
                    }}>
                    {optionsYears
                      ?.filter(item => item !== undefined)
                      .map(item => {
                        return (
                          <MenuItem key={item.value} value={item.value}>
                            {item.value}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
                {formik.touched.months && formik.errors.months ? (
                  <Box
                    sx={{
                      color: theme.palette.red.main,
                      ml: 1,
                    }}>
                    {formik.errors.months}
                  </Box>
                ) : null}
              </Box>

              <ButtonWrapper>
                <ButtonsPrimary
                  sx={{
                    marginTop: '16px',
                    width: '100%',
                    fontSize: '17px',
                    justifyContent: 'center',
                  }}
                  name='Calcular'
                  disableElevation
                  disabled={!(formik.dirty && formik.isValid)}
                  onClick={() => handleSubmit(formik.values)}
                  fullWidth
                />
              </ButtonWrapper>
            </Box>
          </BoxWrapper>
        </ContainerForm>
      </ContentFormContributions>
    </>
  );
};

export default SimulatorFormFunds;
